<template>
  <div class="flex-1 overflow-auto min-h-0">
    <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
      <colgroup>
        <col :style="{ width: nameColumnWidth }" />
        <col style="width: 85px" />
        <col v-for="w in weeksHeaders" :key="w.iso_label" style="width: 80px" />
        <col style="width: 120px" />
      </colgroup>
      <thead class="bg-gray-50 dark:bg-gray-900 sticky top-0 z-40">
        <tr>
          <th
            class="px-4 py-2 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider bg-gray-50 dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700 sticky left-0 z-50"
          >
            <button
              @click="toggleSort('name')"
              class="flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-200"
            >
              <span :class="{ 'font-bold': sortBy === 'name' }">Naam / Rol</span>
              <span v-if="sortBy === 'name'" class="text-xs">
                {{ sortOrder === 'asc' ? '↑' : '↓' }}
              </span>
            </button>
          </th>
          <th
            class="px-2 py-2 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider bg-gray-50 dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700"
          >
            <div class="flex justify-center">
              <button
                @click="toggleSort('match')"
                class="flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-200"
              >
                <span :class="{ 'font-bold': sortBy === 'match' }">Match</span>
                <span v-if="sortBy === 'match'" class="text-xs">
                  {{ sortOrder === 'asc' ? '↑' : '↓' }}
                </span>
              </button>
            </div>
          </th>
          <th
            v-for="(w, idx) in weeksHeaders"
            :key="idx"
            class="px-2 py-2 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider bg-gray-50 dark:bg-gray-900 cursor-help"
            :title="weekRangeTitle(w)"
          >
            {{ w.iso_label }}
          </th>
          <th
            class="px-4 py-2 bg-gray-50 dark:bg-gray-900 border-l border-gray-200 dark:border-gray-700 sticky right-0 z-50"
          ></th>
        </tr>
      </thead>
      <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700">
        <tr
          v-for="emp in employees"
          :key="emp.simplicate_id"
          class="hover:bg-gray-50 dark:hover:bg-gray-700"
        >
          <td
            class="px-4 py-2 sticky left-0 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 z-20 cursor-pointer"
            @click="$emit('open-employee', emp)"
          >
            <div
              class="text-sm font-medium text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 transition-colors flex items-center gap-1.5"
            >
              {{ emp.name }}
              <span
                v-if="emp.simplicate_id === SWP_EMPLOYEE_ID"
                class="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-700 dark:bg-indigo-900 dark:text-indigo-300"
                >SWP</span
              >
            </div>
            <template v-if="emp.simplicate_id !== SWP_EMPLOYEE_ID">
              <div class="space-y-0.5">
                <div
                  v-for="team in getEmployeeTeams(emp)"
                  :key="team"
                  class="text-[0.6rem] text-gray-500 dark:text-gray-400"
                >
                  {{ team }}
                </div>
              </div>
              <div class="text-[0.6rem] text-gray-500 dark:text-gray-400">
                {{ emp.function || '—' }}
              </div>
              <div class="text-[0.6rem] text-gray-500 dark:text-gray-400">
                {{ parseFloat(emp.fte_ratio) || 1.0 }}FTE ({{
                  parseFloat(emp.contractual_hours_per_week) || 40
                }}u/week)
              </div>
            </template>
            <div v-else class="text-[0.6rem] text-gray-500 dark:text-gray-400">
              Externe partner — altijd beschikbaar
            </div>
          </td>
          <td
            class="px-2 py-2 text-center align-middle border-r border-gray-200 dark:border-gray-700"
          >
            <div class="text-sm font-semibold text-gray-900 dark:text-white">
              {{ matchPercent(emp).toFixed(0) }}%
            </div>
          </td>
          <td
            v-for="(w, idx) in weeksHeaders"
            :key="idx"
            class="px-2 py-2 text-center align-middle"
          >
            <button
              class="relative inline-block w-14 text-xs font-semibold text-gray-900 rounded-md py-1 hover:brightness-95"
              :style="{ backgroundColor: bgFor(emp, w) }"
              @click="$emit('open-week', emp, w)"
              :title="weekTitle(emp, w)"
            >
              <span
                v-if="availabilityMode === 'voorgesteld' && hasProposedAssignment(emp, w)"
                class="absolute -top-1 right-[1px] h-2.5 w-2.5 rounded-full bg-yellow-400 ring-2 ring-white"
                aria-label="Voorgesteld deze week"
                title="Voorgesteld deze week"
              />
              <span
                v-if="availabilityMode !== 'definitief' && hasProspectAssignment(emp, w)"
                class="absolute -top-1 -right-1 z-10 h-2.5 w-2.5 rounded-full bg-purple-600 ring-2 ring-white"
                aria-label="Gepresourced deze week"
                title="Gepresourced deze week"
              />
              {{ valueFor(emp, w).toFixed(1) }}d
            </button>
          </td>
          <td
            class="px-4 py-2 text-right sticky right-0 bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 z-20"
          >
            <button
              @mousedown.stop.prevent
              @click.stop.prevent="$emit('select', { employee: emp, dpw: dpw })"
              :disabled="disabled"
              class="px-3 py-1.5 rounded text-white text-xs"
              :class="disabled ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'"
            >
              Toevoegen
            </button>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup>
import { SWP_EMPLOYEE_ID } from '@/constants/swp.js'

const props = defineProps({
  employees: {
    type: Array,
    required: true
  },
  weeksHeaders: {
    type: Array,
    required: true
  },
  dpw: {
    type: Number,
    required: true
  },
  sortBy: {
    type: String,
    required: true
  },
  sortOrder: {
    type: String,
    required: true
  },
  disabled: {
    type: Boolean,
    default: false
  },
  nameColumnWidth: {
    type: String,
    default: '12vw'
  },
  availabilityMode: {
    type: String,
    default: 'presourcing'
  }
})

const emit = defineEmits([
  'select',
  'open-week',
  'open-employee',
  'update:sortBy',
  'update:sortOrder'
])

const toggleSort = field => {
  if (props.sortBy === field) {
    emit('update:sortOrder', props.sortOrder === 'asc' ? 'desc' : 'asc')
  } else {
    emit('update:sortBy', field)
    // For match sorting, default to descending (highest percentage first)
    // For name sorting, default to ascending (A-Z)
    emit('update:sortOrder', field === 'match' ? 'desc' : 'asc')
  }
}

const valueFor = (emp, week) => {
  const w = emp.weeks.find(x => x.week_start === week.week_start)
  return w ? Number(w.available_days || 0) : 0
}

const bgFor = (emp, week) => {
  const v = valueFor(emp, week)
  const mix = (a, b, t) => Math.round(a + (b - a) * t)
  const hex = (r, g, b) =>
    `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`
  const green = [22, 163, 74],
    yellow = [234, 179, 8],
    red = [185, 28, 28]
  let c
  const clampedV = Math.max(0, Math.min(4, v))
  if (clampedV <= 2) {
    const t = clampedV / 2
    c = [mix(green[0], yellow[0], t), mix(green[1], yellow[1], t), mix(green[2], yellow[2], t)]
  } else {
    const t = (clampedV - 2) / 2
    c = [mix(yellow[0], red[0], t), mix(yellow[1], red[1], t), mix(yellow[2], red[2], t)]
  }
  c = c.map(x => Math.min(255, Math.round(x + (255 - x) * 0.15)))
  return hex(c[0], c[1], c[2])
}

const hasProspectAssignment = (emp, week) => {
  const w = emp.weeks.find(x => x.week_start === week.week_start)
  return !!(w && Number(w.assignment_hours) > 0)
}

const hasProposedAssignment = (emp, week) => {
  const w = emp.weeks.find(x => x.week_start === week.week_start)
  if (!w) return false
  return Number(w.proposed_hours) > 0 || w.has_local_proposal === true
}

const formatDateFull = input => {
  if (!input) return ''
  const s = String(input).split('T')[0]
  const d = new Date(`${s}T00:00:00`)
  if (isNaN(d)) return s
  return `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`
}

const weekRangeTitle = week => {
  if (!week?.week_start || !week?.week_end) return ''
  return `Week ${week.iso_label ?? week.iso_week}: ${formatDateFull(week.week_start)} t/m ${formatDateFull(week.week_end)}`
}

const weekTitle = (emp, week) => {
  const w = emp.weeks.find(x => x.week_start === week.week_start)
  if (!w) return `Bekijk boekingen ${week.iso_label}`

  const availableHours = (Number(w.available_days || 0) * 8).toFixed(1)
  const assigned = w.assignment_hours
    ? `, ${Number(w.assignment_hours).toFixed(1)}u gepresourced`
    : ''

  return `${availableHours}u beschikbaar${assigned}`
}

// Match percentage: % of weeks where available_days >= DPW
// SWP always shows 0% match
const matchPercent = emp => {
  if (emp.simplicate_id === SWP_EMPLOYEE_ID) return 0
  const weeks = props.weeksHeaders
  const required = Number(props.dpw)
  if (!weeks.length || !required) return 0
  let ok = 0
  for (const w of weeks) {
    if (valueFor(emp, w) >= required) ok++
  }
  return (ok / weeks.length) * 100
}

// Helper function to get employee teams
const getEmployeeTeams = employee => {
  if (!employee.teams || employee.teams.length === 0) {
    return ['Geen team']
  }
  return employee.teams.map(team => team.name)
}
</script>
